<template>
  <div class="header-shell" :class="{ 'dark-mode': isDarkMode }">
    <div class="tabs header-bar">
    <!-- Left Section: Navigation Tabs -->
    <div class="tabs-group">
      <button 
        @click="navigateTo('/binance')" 
        :class="{ active: selectedTab === 'binancenewui', 'tab-binancenewui': true }"
      >
        Binance
      </button>
      <button 
        @click="navigateTo('/binance-alpha')" 
        :class="{ active: selectedTab === 'binancealpha', 'tab-binancealpha': true }"
      >
        Binance Alpha
      </button>
      <button 
        @click="navigateTo('/mexc')" 
        :class="{ active: selectedTab === 'mexc', 'tab-mexc': true }"
      >
        Mexc
      </button>
      <button 
        @click="navigateTo('/bybit')" 
        :class="{ active: selectedTab === 'bybit', 'tab-bybit': true }"
      >
        Bybit
      </button>
      <button 
        @click="navigateTo('/binance-ls-compare')" 
        :class="{ active: selectedTab === 'lscompare', 'tab-lscompare': true }"
      >
        Phân tích
      </button>
    </div>
    </div>

    <!-- Right Section: Controls & Profile -->
    <div class="controls-group">
      <!-- Theme Toggle Button -->
      <button 
        @click="$emit('toggle-dark-mode')" 
        class="dark-mode-btn" 
        :title="isDarkMode ? 'Tat Dark Mode' : 'Bat Dark Mode'"
      >
        <i :class="['fa-solid', isDarkMode ? 'fa-sun' : 'fa-moon', 'theme-icon']"></i>
      </button>

      <!-- User Account Widget -->
      <button 
        ref="userWidget" 
        class="user-widget" 
        :class="{ active: isDropdownOpen }"
        @click="toggleDropdown"
        :title="currentUser.fullName || currentUser.email || 'Account'"
      >
        <div class="avatar-container">
          <span class="avatar-initial">{{ accountInitial }}</span>
        </div>
        <i :class="['fa-solid', isDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down', 'chevron-icon']"></i>
      </button>

      <!-- Theme Toggle Button -->
      <button 
        @click="$emit('toggle-dark-mode')" 
        class="dark-mode-btn" 
        :title="isDarkMode ? 'Tắt Dark Mode' : 'Bật Dark Mode'"
      >
        <span v-if="isDarkMode" class="theme-icon">☀️</span>
        <span v-else class="theme-icon">🌙</span>
      </button>

      <!-- Dropdown Menu -->
      <transition name="fade-slide">
        <div 
          v-if="isDropdownOpen" 
          ref="userDropdown" 
          class="dropdown-menu"
        >
          <div class="account-summary">
            <div class="summary-avatar">{{ accountInitial }}</div>
            <div class="summary-text">
              <span class="summary-name">{{ currentUser.fullName || currentUser.email || 'Account' }}</span>
              <span class="summary-role">{{ currentUser.role || 'USER' }}</span>
            </div>
          </div>

          <div class="dropdown-divider"></div>

          <div class="dropdown-item" @click="handleItemClick('profile')">
            <div class="item-icon-wrapper profile-icon">
              <i class="fa-solid fa-user"></i>
            </div>
            <div class="item-text">
              <span class="item-title">Profile</span>
              <span class="item-description">View and update your personal information</span>
            </div>
          </div>

          <div v-if="isAdmin" class="dropdown-item" @click="handleItemClick('management')">
            <div class="item-icon-wrapper management-icon">
              <i class="fa-solid fa-gear"></i>
            </div>
            <div class="item-text">
              <span class="item-title">
                Account Management
                <span class="admin-badge">Admin</span>
              </span>
              <span class="item-description">Manage users and system permissions</span>
            </div>
          </div>

          <div class="dropdown-divider"></div>

          <div class="dropdown-item logout-item" @click="handleItemClick('logout')">
            <div class="item-icon-wrapper logout-icon">
              <i class="fa-solid fa-right-from-bracket"></i>
            </div>
            <div class="item-text">
              <span class="item-title">Log out</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';
import { getCurrentUser, isAdmin } from '@/services/authStorage';

export default {
  name: 'AppHeader',
  props: {
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedTab: 'binancenewui',
      isDropdownOpen: false,
      currentUser: getCurrentUser() || {}
    };
  },
  computed: {
    accountInitial() {
      const displayName = this.currentUser.fullName || this.currentUser.email || 'A';
      return displayName.trim().charAt(0).toUpperCase();
    },
    isAdmin() {
      return isAdmin();
    }
  },
  mounted() {
    this.updateTabFromRoute(this.$route.path);
    document.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('auth-changed', this.refreshCurrentUser);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('auth-changed', this.refreshCurrentUser);
  },
  methods: {
    refreshCurrentUser() {
      this.currentUser = getCurrentUser() || {};
    },
    navigateTo(path) {
      this.$router.push(path);
    },
    toggleDropdown(event) {
      event.stopPropagation();
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    handleOutsideClick(event) {
      const dropdown = this.$refs.userDropdown;
      const widget = this.$refs.userWidget;
      if (
        this.isDropdownOpen &&
        dropdown &&
        !dropdown.contains(event.target) &&
        widget &&
        !widget.contains(event.target)
      ) {
        this.isDropdownOpen = false;
      }
    },
    async handleItemClick(action) {
      this.closeDropdown();
      if (action === 'profile') {
        this.navigateTo('/profile');
      } else if (action === 'management') {
        this.navigateTo('/admin/accounts');
      } else if (action === 'logout') {
        await AuthService.logout();
        this.$router.push('/login');
      }
    },
    updateTabFromRoute(path) {
      if (path.startsWith('/binance-alpha')) this.selectedTab = 'binancealpha';
      else if (path.startsWith('/binance-ls-compare')) this.selectedTab = 'lscompare';
      else if (path.startsWith('/binance-new-ui') || path.startsWith('/binance')) this.selectedTab = 'binancenewui';
      else if (path === '/mexc') this.selectedTab = 'mexc';
      else if (path === '/bybit') this.selectedTab = 'bybit';
      else if (path === '/all-coins') this.selectedTab = 'allcoins';
    }
  },
  watch: {
    '$route'(to) {
      this.currentUser = getCurrentUser() || {};
      this.updateTabFromRoute(to.path);
    }
  }
};
</script>

<style scoped>
.header-shell {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  gap: 16px !important;
  padding: 8px 16px !important;
  flex-wrap: nowrap !important;
  position: relative;
  z-index: 5000;
  box-shadow: 0 8px 24px rgba(147, 51, 234, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-shell.dark-mode {
  background: #1e2329 !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25) !important;
}

/* Override default .tabs styling for a custom header bar */
.tabs.header-bar {
  flex: 0 1 1080px;
  width: min(1080px, calc(100% - 180px));
  min-width: 0;
  margin: 0 auto;
}

/* Tabs Group (Left) */
.tabs-group {
  display: flex;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.tabs-group button {
  flex: 1 1 0 !important;
  min-width: 0;
  padding: 10px 12px !important;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.45);
  color: #64748b;
  transition: all 0.2s ease;
  line-height: 1;
  white-space: nowrap;
}

.tabs-group button:hover {
  background: rgba(255, 255, 255, 0.75);
  color: #1e293b;
  transform: translateY(-1px);
}

.tabs-group button.active {
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(63, 129, 229, 0.3) !important;
  transform: translateY(-1px);
}

.header-shell.dark-mode .tabs-group button {
  background: rgba(255, 255, 255, 0.05);
  color: #94a3b8;
}

.header-shell.dark-mode .tabs-group button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #eaecef;
}

.header-shell.dark-mode .tabs-group button.active {
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%) !important;
  color: white !important;
}

/* Controls Group (Right) */
.controls-group {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
}

.controls-group .dark-mode-btn + .user-widget + .dark-mode-btn {
  display: none;
}

/* User Account Pill Widget */
.user-widget {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px 3px 3px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.user-widget:hover, .user-widget.active {
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.header-shell.dark-mode .user-widget {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.header-shell.dark-mode .user-widget:hover,
.header-shell.dark-mode .user-widget.active {
  background: rgba(255, 255, 255, 0.12);
}

/* Avatar circle */
.avatar-container {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.avatar-initial {
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

/* User Details */
.user-info {
  display: flex;
  flex-direction: column;
  text-align: left;
  min-width: 0;
}

.user-name {
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.15;
}

.user-role {
  font-size: 9px;
  font-weight: 500;
  color: #64748b;
  margin-top: 0;
}

.chevron-icon {
  font-size: 9px;
  color: #64748b;
  transition: transform 0.2s ease;
  margin-left: 2px;
}

.header-shell.dark-mode .user-name {
  color: #eaecef;
}

.header-shell.dark-mode .user-role {
  color: #94a3b8;
}

.header-shell.dark-mode .chevron-icon {
  color: #94a3b8;
}

/* Dark Mode circular button */
.dark-mode-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #2b3139;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  padding: 0 !important;
}

.dark-mode-btn:hover {
  transform: scale(1.06) rotate(12deg);
  background: #181a20;
}

.header-shell.dark-mode .dark-mode-btn {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.header-shell.dark-mode .dark-mode-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: scale(1.06) rotate(-12deg);
}

.theme-icon {
  font-size: 14px;
  line-height: 1;
}

/* Dropdown Menu Container */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  width: 260px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 8px;
  z-index: 5001;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transform-origin: top right;
}

.account-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
}

.summary-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 800;
}

.summary-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.summary-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e293b;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
}

.summary-role {
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
}

.header-shell.dark-mode .summary-name {
  color: #eaecef;
}

.header-shell.dark-mode .summary-role {
  color: #94a3b8;
}

.header-shell.dark-mode .dropdown-menu {
  background: #1e2329;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Dropdown Items */
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.dropdown-item:hover {
  background: #f1f5f9;
}

.header-shell.dark-mode .dropdown-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* Dropdown Icons */
.item-icon-wrapper {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}

.profile-icon {
  background: #ede9fe;
  color: #8b5cf6;
}

.management-icon {
  background: #e2e8f0;
  color: #475569;
}

.logout-icon {
  background: #fee2e2;
  color: #ef4444;
}

.header-shell.dark-mode .profile-icon {
  background: rgba(139, 92, 246, 0.15);
  color: #a78bfa;
}

.header-shell.dark-mode .management-icon {
  background: rgba(71, 85, 105, 0.15);
  color: #94a3b8;
}

.header-shell.dark-mode .logout-icon {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

/* Item Text Structure */
.item-text {
  display: flex;
  flex-direction: column;
}

.item-title {
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.header-shell.dark-mode .item-title {
  color: #eaecef;
}

.item-description {
  font-size: 10px;
  color: #64748b;
  line-height: 1.3;
  margin-top: 2px;
}

.header-shell.dark-mode .item-description {
  color: #94a3b8;
}

/* Admin Badge Tag */
.admin-badge {
  background: #ede9fe;
  color: #7c3aed;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
}

.header-shell.dark-mode .admin-badge {
  background: rgba(124, 58, 237, 0.2);
  color: #c084fc;
}

/* Logout Item styling */
.logout-item .item-title {
  color: #ef4444;
}

.header-shell.dark-mode .logout-item .item-title {
  color: #f87171;
}

/* Divider line */
.dropdown-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 6px 0;
}

.header-shell.dark-mode .dropdown-divider {
  background: rgba(255, 255, 255, 0.08);
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

/* Responsive CSS */
@media (max-width: 900px) {
  .header-shell {
    flex-wrap: wrap !important;
    gap: 12px !important;
  }
  .tabs.header-bar {
    order: 2;
    width: 100%;
  }
  .tabs-group {
    width: 100%;
  }
  .tabs-group button {
    font-size: 12px;
    padding: 9px 8px !important;
  }
  .controls-group {
    order: 1;
    width: 100%;
    justify-content: flex-end;
    position: static;
    transform: none;
  }
  .dropdown-menu {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    transform-origin: top center;
  }
  .fade-slide-enter-from,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translate(-50%, -8px) scale(0.95);
  }
}
</style>
